import React, { useState, useRef, useEffect, useCallback } from 'react';

const BLOCK_TYPES = {
    MainNavigationLink: 'MainNavigationLink',
    SecondaryNavigationLink: 'SecondaryNavigationLink',
    MegaMenuColumn: 'MegaMenuColumn',
    MegaMenuSubColumn: 'MegaMenuSubColumn',
    MegaMenuTitleLink: 'MegaMenuTitleLink',
    MegaMenuLinkItem: 'MegaMenuLinkItem',
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const SPAN_CLASS = {
    1: 'tw-col-span-1',
    2: 'tw-col-span-2',
    3: 'tw-col-span-3',
    4: 'tw-col-span-4',
    5: 'tw-col-span-5',
    6: 'tw-col-span-6',
};

const GRID_COLS_CLASS = {
    1: 'tw-grid-cols-1',
    2: 'tw-grid-cols-2',
    3: 'tw-grid-cols-3',
    4: 'tw-grid-cols-4',
    5: 'tw-grid-cols-5',
    6: 'tw-grid-cols-6',
};

const ROW_BASE = 'tw-flex tw-items-center tw-min-h-6 tw-text-base tw-leading-tight tw-text-black';
const ROW_CLASS = `${ROW_BASE} tw-py-0.5`;

const MegaMenuLinkItem = ({ item }) => {
    if (!item.url) {
        return <span className={`${ROW_BASE} tw-font-normal`}>{item.text}</span>;
    }
    return (
        <a
            href={item.url}
            className={`${ROW_BASE} tw-font-normal hover:tw-underline`}
        >
            {item.text}
        </a>
    );
};

const MegaMenuTitleLink = ({ item }) => {
    const linkItems = (item.children || []).filter(c => c.type === BLOCK_TYPES.MegaMenuLinkItem);
    const hasTitle = !!item.text;

    const titleContent = !hasTitle ? null : item.url ? (
        <a
            href={item.url}
            className={`${ROW_CLASS} tw-font-semibold hover:tw-underline`}
        >
            {item.text}
        </a>
    ) : (
        <span className={`${ROW_CLASS} tw-font-semibold`}>{item.text}</span>
    );

    return (
        <div className="tw-flex tw-flex-col tw-gap-2">
            {titleContent}
            {linkItems.map((child, idx) => (
                <MegaMenuLinkItem key={idx} item={child} />
            ))}
        </div>
    );
};

const renderColumnBody = (children) => {
    const sections = [];
    let currentSection = null;

    children.forEach((child) => {
        if (child.type === BLOCK_TYPES.MegaMenuTitleLink) {
            currentSection = { type: 'title', item: child };
            sections.push(currentSection);
        } else if (child.type === BLOCK_TYPES.MegaMenuLinkItem) {
            if (!currentSection || currentSection.type !== 'items') {
                currentSection = { type: 'items', items: [] };
                sections.push(currentSection);
            }
            currentSection.items.push(child);
        }
    });

    return (
        <div className="tw-flex tw-flex-col tw-gap-4">
            {sections.map((section, idx) => {
                if (section.type === 'title') {
                    return <MegaMenuTitleLink key={idx} item={section.item} />;
                }
                return (
                    <div key={idx} className="tw-flex tw-flex-col tw-gap-2">
                        {section.items.map((child, i) => (
                            <MegaMenuLinkItem key={i} item={child} />
                        ))}
                    </div>
                );
            })}
        </div>
    );
};

const MegaMenuSubColumn = ({ subColumn, isLast, hideDivider }) => {
    const borderClass = !isLast && !hideDivider ? 'main-nav__divider' : '';
    const rightPad = isLast ? '' : 'tw-pr-6';

    return (
        <div className={`tw-flex tw-flex-col ${borderClass} ${rightPad}`}>
            {renderColumnBody(subColumn.children || [])}
        </div>
    );
};

const HEADING_CLASS = 'tw-mt-0 tw-mb-2 tw-flex tw-items-center tw-min-h-6 tw-py-0.5 tw-text-lg tw-font-semibold tw-leading-tight tw-text-black';

const renderHeading = (item) => {
    if (!item.heading) return null;
    if (item.url) {
        return (
            <a
                href={item.url}
                className={`${HEADING_CLASS} hover:tw-underline`}
            >
                {item.heading}
            </a>
        );
    }
    return <div className={HEADING_CLASS}>{item.heading}</div>;
};

const renderColumnContent = (children, options = {}) => {
    const { hideSubColumnDividers = false } = options;
    const subColumns = children.filter(c => c.type === BLOCK_TYPES.MegaMenuSubColumn);
    const nestedColumns = children.filter(c => c.type === BLOCK_TYPES.MegaMenuColumn);

    if (subColumns.length > 0) {
        return (
            <div className={`tw-grid tw-gap-6 ${GRID_COLS_CLASS[clamp(subColumns.length, 1, 6)]}`}>
                {subColumns.map((sub, idx) => (
                    <MegaMenuSubColumn
                        key={idx}
                        subColumn={sub}
                        isLast={idx === subColumns.length - 1}
                        hideDivider={hideSubColumnDividers}
                    />
                ))}
            </div>
        );
    }

    if (nestedColumns.length > 0) {
        return (
            <div className="tw-flex tw-flex-col">
                {nestedColumns.map((nested, idx) => (
                    <div key={idx} className={idx === 0 ? '' : 'tw-mt-4'}>
                        {renderHeading(nested)}
                        {renderColumnContent(nested.children || [], { hideSubColumnDividers: !!nested.hideSubColumnDividers })}
                    </div>
                ))}
            </div>
        );
    }

    return renderColumnBody(children);
};

const MegaMenuColumn = ({ column, isLast, centered }) => {
    const span = clamp(column.columnSpan || 1, 1, 6);
    const spanClass = centered ? '' : SPAN_CLASS[span];
    const children = column.children || [];

    const borderClass = isLast ? '' : 'main-nav__divider';
    const rightPad = isLast ? '' : 'tw-pr-6';

    return (
        <div className={`${spanClass} tw-flex tw-flex-col ${borderClass} ${rightPad}`}>
            {renderHeading(column)}
            {renderColumnContent(children, { hideSubColumnDividers: !!column.hideSubColumnDividers })}
        </div>
    );
};

const MegaMenuPanel = ({ item, onClose }) => {
    const columns = clamp(item.columns || 5, 1, 6);
    const gridColsClass = GRID_COLS_CLASS[columns];
    const columnBlocks = (item.children || []).filter(c => c.type === BLOCK_TYPES.MegaMenuColumn);
    const centerColumns = !!item.centerColumns;

    if (columnBlocks.length === 0) return null;

    // In centered mode, each column's width matches what it would get in non-centered grid mode:
    // a base "ideal" column width times its columnSpan, plus inner gaps for spanned columns.
    // This keeps subcolumn spacing identical regardless of CenterMegaMenuColumns.
    const containerInnerWidth = 1389 - 48 - 16; // tw-pl-12 (48px) + tw-pr-4 (16px)
    const gapPx = 24; // tw-gap-6
    const idealColumnWidth = Math.floor((containerInnerWidth - (columns - 1) * gapPx) / columns);
    const layoutClass = centerColumns
        ? 'tw-grid tw-gap-6 tw-justify-center'
        : `tw-grid tw-gap-6 ${gridColsClass}`;
    const layoutStyle = centerColumns
        ? {
            gridTemplateColumns: columnBlocks
                .map((col) => {
                    const span = clamp(col.columnSpan || 1, 1, 6);
                    const width = span * idealColumnWidth + (span - 1) * gapPx;
                    return `minmax(0, ${width}px)`;
                })
                .join(' '),
        }
        : undefined;

    return (
        <div
            className="tw-absolute tw-left-0 tw-right-0 tw-top-full tw-z-40 tw-bg-white tw-shadow-lg"
            onMouseLeave={onClose}
        >
            <div className="tw-mx-auto tw-max-w-[1389px] tw-pl-12 tw-pr-4 tw-py-6">
                <div className={layoutClass} style={layoutStyle}>
                    {columnBlocks.map((col, idx) => (
                        <MegaMenuColumn
                            key={idx}
                            column={col}
                            isLast={idx === columnBlocks.length - 1}
                            centered={centerColumns}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

const isTouchDevice = () => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(hover: none)').matches
        || window.matchMedia('(pointer: coarse)').matches;
};

const HOVER_INTENT_DELAY_MS = 200;

const MainMenuItem = React.forwardRef(({ item, index, isOpen, onOpen, onClose, onCloseAll, onFocusNext, onFocusPrev, onArrowDown, panelId }, ref) => {
    const hasMegaMenu = (item.children || []).some(c => c.type === BLOCK_TYPES.MegaMenuColumn);
    const hoverTimer = useRef(null);

    const clearHoverTimer = () => {
        if (hoverTimer.current) {
            clearTimeout(hoverTimer.current);
            hoverTimer.current = null;
        }
    };

    useEffect(() => () => clearHoverTimer(), []);

    const handleActivate = () => {
        if (hasMegaMenu) {
            onOpen();
        } else if (onCloseAll) {
            onCloseAll();
        }
    };

    const handleMouseEnter = () => {
        clearHoverTimer();
        if (isOpen) {
            handleActivate();
            return;
        }
        hoverTimer.current = setTimeout(() => {
            handleActivate();
        }, HOVER_INTENT_DELAY_MS);
    };

    const handleMouseLeave = () => {
        clearHoverTimer();
    };

    const handleClick = (e) => {
        if (!hasMegaMenu) return;
        if (!isTouchDevice()) return;
        if (isOpen) return;
        e.preventDefault();
        onOpen();
    };

    const handleKeyDown = (e) => {
        switch (e.key) {
            case 'ArrowDown':
                if (hasMegaMenu) {
                    e.preventDefault();
                    onOpen();
                    onArrowDown && onArrowDown();
                }
                break;
            case 'ArrowRight':
                e.preventDefault();
                onFocusNext && onFocusNext();
                break;
            case 'ArrowLeft':
                e.preventDefault();
                onFocusPrev && onFocusPrev();
                break;
            case 'Escape':
                if (isOpen) {
                    e.preventDefault();
                    onClose && onClose();
                }
                break;
            default:
                break;
        }
    };

    const baseClass = `main-nav__item tw-flex tw-items-center tw-gap-1 tw-h-12 tw-px-3 tw-py-2 tw-text-lg tw-font-bold tw-leading-tight tw-text-black tw-whitespace-nowrap ${isOpen ? 'main-nav__item--active' : ''}`;

    return (
        <div
            className="tw-relative"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            role="none"
        >
            {item.url ? (
                <a
                    ref={ref}
                    href={item.url}
                    onFocus={handleActivate}
                    onKeyDown={handleKeyDown}
                    onClick={handleClick}
                    aria-haspopup={hasMegaMenu ? 'true' : undefined}
                    aria-expanded={hasMegaMenu ? (isOpen ? 'true' : 'false') : undefined}
                    aria-controls={hasMegaMenu && isOpen ? panelId : undefined}
                    data-main-menu-item-index={index}
                    className={baseClass}
                >
                    {item.text}
                </a>
            ) : (
                <button
                    type="button"
                    ref={ref}
                    onFocus={handleActivate}
                    onKeyDown={handleKeyDown}
                    onClick={(e) => { e.preventDefault(); handleActivate(); }}
                    aria-haspopup={hasMegaMenu ? 'true' : undefined}
                    aria-expanded={hasMegaMenu ? (isOpen ? 'true' : 'false') : undefined}
                    aria-controls={hasMegaMenu && isOpen ? panelId : undefined}
                    data-main-menu-item-index={index}
                    className={`${baseClass} tw-bg-transparent tw-border-t-0 tw-border-l-0 tw-border-r-0 tw-cursor-pointer`}
                >
                    {item.text}
                </button>
            )}
        </div>
    );
});

const MainNavigation = ({ data }) => {
    const [openIndex, setOpenIndex] = useState(-1);
    const containerRef = useRef(null);
    const panelRef = useRef(null);
    const itemRefs = useRef([]);
    const closeTimer = useRef(null);
    const panelId = 'main-nav__megapanel';

    const focusItem = (idx) => {
        const el = itemRefs.current[idx];
        if (el && el.focus) el.focus();
    };

    const focusFirstPanelLink = () => {
        requestAnimationFrame(() => {
            if (!panelRef.current) return;
            const firstLink = panelRef.current.querySelector('a[href]');
            if (firstLink) firstLink.focus();
        });
    };

    const clearCloseTimer = useCallback(() => {
        if (closeTimer.current) {
            clearTimeout(closeTimer.current);
            closeTimer.current = null;
        }
    }, []);

    const scheduleClose = useCallback(() => {
        clearCloseTimer();
        closeTimer.current = setTimeout(() => setOpenIndex(-1), 120);
    }, [clearCloseTimer]);

    useEffect(() => () => clearCloseTimer(), [clearCloseTimer]);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape' && openIndex >= 0) {
                clearCloseTimer();
                const returnIdx = openIndex;
                setOpenIndex(-1);
                requestAnimationFrame(() => focusItem(returnIdx));
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [clearCloseTimer, openIndex]);

    const handleBlur = useCallback((e) => {
        const nextFocus = e.relatedTarget;
        if (!containerRef.current || !nextFocus || !containerRef.current.contains(nextFocus)) {
            scheduleClose();
        }
    }, [scheduleClose]);

    const items = (data && data.items) || [];
    const primary = items.filter(i => i.type === BLOCK_TYPES.MainNavigationLink);
    const openItem = openIndex >= 0 ? primary[openIndex] : null;

    useEffect(() => {
        if (openItem) {
            document.body.classList.add('nav-open');
        } else {
            document.body.classList.remove('nav-open');
        }
        return () => {
            document.body.classList.remove('nav-open');
        };
    }, [openItem]);

    return (
        <nav
            ref={containerRef}
            className="tw-relative tw-w-full"
            aria-label="Huvudmeny"
            onMouseLeave={scheduleClose}
            onMouseEnter={clearCloseTimer}
            onFocus={clearCloseTimer}
            onBlur={handleBlur}
        >
            <div className="tw-bg-white main-nav__row-border">
                <div className="tw-mx-auto tw-max-w-[1389px] tw-flex tw-items-center tw-justify-center tw-gap-3 tw-px-3">
                    {primary.map((item, idx) => (
                        <MainMenuItem
                            key={idx}
                            ref={(el) => { itemRefs.current[idx] = el; }}
                            item={item}
                            index={idx}
                            panelId={panelId}
                            isOpen={openIndex === idx}
                            onOpen={() => { clearCloseTimer(); setOpenIndex(idx); }}
                            onClose={() => {
                                clearCloseTimer();
                                setOpenIndex(-1);
                                requestAnimationFrame(() => focusItem(idx));
                            }}
                            onCloseAll={() => { clearCloseTimer(); setOpenIndex(-1); }}
                            onFocusNext={() => focusItem(Math.min(idx + 1, primary.length - 1))}
                            onFocusPrev={() => focusItem(Math.max(idx - 1, 0))}
                            onArrowDown={focusFirstPanelLink}
                        />
                    ))}
                </div>
            </div>
            {openItem ? (
                <div ref={panelRef} id={panelId} role="region" aria-label={openItem.text}>
                    <MegaMenuPanel item={openItem} onClose={scheduleClose} />
                </div>
            ) : null}
        </nav>
    );
};

export default MainNavigation;
